/**
 * Persistent admin navigation strip rendered above every admin page —
 * both V2 routes (/admin/<slug>) and the legacy Operator Console
 * (/admin?tab=<slug>). Now the single source of truth for admin
 * navigation chrome; OperatorConsole no longer renders its own TabsList.
 *
 * Design intent: editors should NOT feel like V2 admin is a separate
 * world from the legacy Operator Console. One slim sticky strip,
 * identical labels/icons regardless of where you are.
 *
 * Routing:
 *   - V2 sections (Properties / Lessors / Translations) → V2 routes
 *     under /admin/...
 *   - Legacy sections (Tenants / Payments / Documents / Templates /
 *     Support / Settings / Analytics) → /admin?tab=<tab-name>, which
 *     OperatorConsole reads via useSearchParams to switch its internal
 *     Radix Tabs (TabsContent panels are still controlled by `?tab=`;
 *     only the visual chrome moved up here).
 *
 * Active state is derived from the current URL, not internal state — a
 * direct deep-link to /admin/properties/123 still highlights "Properties"
 * because of the path-prefix match.
 *
 * Support badge: WhatsApp-style red pip, count fed by useSupportUnreadCount.
 * Lives here (not just on the legacy console) so admins working in V2
 * see new tickets/messages without visiting the old console first.
 */
import { useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  BarChart3,
  Brain,
  BellRing,
  Building2,
  CreditCard,
  FileText,
  Banknote,
  Gift,
  Headset,
  History,
  Landmark,
  Languages,
  Settings,
  Users,
  UsersRound,
  UserCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSupportUnreadCount } from "@/hooks/useSupportUnreadCount";
import { useReconciliationReviewCount } from "@/hooks/v2/useReconciliation";
import { useOwnerUnreadNotificationCount } from "@/hooks/v2/useOwnerNotifications";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AdminUserMenu } from "@/components/admin/v2/AdminUserMenu";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePendingSubmissionCount } from "@/hooks/v2/usePropertySubmissions";
import {
  isAdminBrainEnabled,
  isOwnerPortalSectionsEnabled,
} from "@/lib/featureFlags";
import { useBrainBadgeCount } from "@/hooks/brain/useBrainDeck";
import { useOwnerPortalV3 } from "@/hooks/owner/useOwnerPortalV3";
import { OwnerPortalSidebar } from "./OwnerPortalSidebar";
import { BrandWordmark } from "@/components/BrandWordmark";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { BrainReturnStrip } from "@/components/admin/brain/BrainReturnStrip";
import { SupportNotificationsBell } from "@/components/admin/support/SupportNotificationsBell";

/** Which count drives a nav item's pip. */
export type NavBadge =
  "support" | "ownerNotifications" | "reconciliation" | "submissions" | "brain";

/**
 * A nav item's red pip: the count (capped at 99+), or null at zero or while
 * the count is unknown (null). With `incompleteAria` the count is known to be
 * low (a source failed): the number stays plain (Neel, Brain v1.5 — no "!")
 * and the screen-reader label says it may be incomplete.
 */
export function navPip(
  count: number | null,
  ariaNoun: string,
  incompleteAria?: string | null
): { text: string; ariaLabel: string } | null {
  const has = count != null && count > 0;
  const n = has && count > 99 ? "99+" : String(count);
  if (!has) return null;
  const aria = `${count} ${ariaNoun}`;
  return {
    text: n,
    ariaLabel: incompleteAria ? `${aria}. ${incompleteAria}` : aria,
  };
}

export type RouteItem = {
  label: string;
  /** Optional i18n key — when present, looked up via t(i18nKey, label). */
  i18nKey?: string;
  icon: LucideIcon;
  /** "v2" lives at /admin/<slug>, "legacy" lives at /admin?tab=<slug>. */
  kind: "v2" | "legacy";
  /** For v2: the path prefix. For legacy: the ?tab= value. */
  slug: string;
  /**
   * Which count drives this item's pip. One source per item:
   *   support            → useSupportUnreadCount
   *   ownerNotifications → useOwnerUnreadNotificationCount (unread owner-lifecycle mails)
   *   reconciliation     → useReconciliationReviewCount (needs_review bank matches)
   *   submissions        → usePendingSubmissionCount (owner-submitted properties
   *                        nobody has reviewed; staff-side only)
   *   brain              → useBrainBadgeCount (open colivIQ cards)
   */
  badge?: NavBadge;
  /** Hide from admins without the super_admin/tech role (audit tooling). */
  requiresTechOrSuperAdmin?: boolean;
  /**
   * Hide from console accounts without a staff role (super_admin | tech |
   * admin). Directory carries this because it used to be a TAB that such
   * accounts were never shown — the page must not become a way in that the tab
   * wasn't.
   */
  requiresStaffRole?: boolean;
  /**
   * Retired screen: kept in the list so it can be brought back by deleting
   * one line, but never rendered in the nav. See COL-462 (Payments).
   */
  retired?: boolean;
};

/**
 * Order matches the legacy OperatorConsole's tab order so muscle memory
 * transfers. V2 sections (Properties + Lessors + Translations) are
 * inserted in their natural slots; Support uses the legacy `issues`
 * tab slug — the tab is labelled "Support" in the UI but the route is
 * historical (/admin?tab=issues).
 */
/** Slugs the free-tier flag controls — see isOwnerPortalSectionsEnabled. */
const OWNER_FREE_TIER_SLUGS = new Set(["documents", "payouts", "refer"]);

const OWNER_NAV: RouteItem[] = [
  {
    label: "Analytics & reporting",
    i18nKey: "admin.nav.ownerAnalytics",
    icon: BarChart3,
    kind: "legacy",
    slug: "analytics",
  },
  {
    label: "My properties",
    i18nKey: "admin.nav.myProperties",
    icon: Building2,
    kind: "v2",
    slug: "properties",
  },
  {
    label: "Documents",
    i18nKey: "admin.nav.ownerDocuments",
    icon: FileText,
    kind: "v2",
    slug: "documents",
  },
  {
    label: "Payouts",
    i18nKey: "admin.nav.ownerPayouts",
    icon: Banknote,
    kind: "v2",
    slug: "payouts",
  },
  {
    label: "Notifications",
    i18nKey: "admin.nav.ownerNotifications",
    icon: BellRing,
    kind: "v2",
    slug: "notifications",
    badge: "ownerNotifications",
  },
  {
    label: "Refer a friend",
    i18nKey: "admin.nav.ownerReferrals",
    icon: Gift,
    kind: "v2",
    slug: "refer",
  },
  {
    label: "Settings",
    i18nKey: "admin.nav.settings",
    icon: Settings,
    kind: "legacy",
    slug: "settings",
  },
];

export const STAFF_NAV_ITEMS: RouteItem[] = [
  // colivIQ Brain (COL-527): the admin to-do deck, first because it is where
  // the day starts. Behind VITE_ADMIN_BRAIN; staff-only via BrainRouteGate.
  ...(isAdminBrainEnabled()
    ? [
        {
          label: "colivIQ",
          i18nKey: "admin.nav.brain",
          icon: Brain,
          kind: "v2",
          slug: "brain",
          badge: "brain",
        } satisfies RouteItem,
      ]
    : []),
  {
    label: "Analytics",
    i18nKey: "admin.nav.analytics",
    icon: BarChart3,
    kind: "legacy",
    slug: "analytics",
  },
  {
    label: "Tenants",
    i18nKey: "admin.nav.tenants",
    icon: Users,
    kind: "legacy",
    slug: "tenants",
  },
  // Owners sits beside Tenants because that is how the work pairs up: both are
  // directories of people, read one after the other.
  {
    label: "Owners",
    i18nKey: "admin.nav.owners",
    icon: UsersRound,
    kind: "v2",
    slug: "owners",
  },
  {
    label: "Properties",
    i18nKey: "admin.nav.properties",
    icon: Building2,
    kind: "v2",
    slug: "properties",
    badge: "submissions",
  },
  // Retired 2026-09 (COL-462). Reconciliation covers direct transfers, and
  // direct debits / card payments are marked on the tenant, owner and
  // operator profiles. The hub itself is untouched — drop `retired` to
  // bring the tab back.
  {
    label: "Payments",
    i18nKey: "admin.nav.payments",
    icon: CreditCard,
    kind: "legacy",
    slug: "payments",
    retired: true,
  },
  {
    label: "Reconciliation",
    i18nKey: "admin.nav.reconciliation",
    icon: Landmark,
    kind: "v2",
    slug: "reconciliation",
    badge: "reconciliation",
  },
  // CoLiviq-staff only: manage operator orgs + review their bank-connect
  // onboarding (Neel #3) — a top-level tab. Hidden from operators via the
  // OperatorRouteGate on /admin/operators.
  {
    label: "Operators",
    i18nKey: "admin.nav.operatorsAdmin",
    icon: Building2,
    kind: "v2",
    slug: "operators",
    requiresStaffRole: true,
  },
  {
    label: "Support",
    i18nKey: "admin.nav.support",
    icon: Headset,
    kind: "legacy",
    slug: "issues",
    badge: "support",
  },
  // "Roles & access" used to sit here. Its three sections now live on
  // Owners & access as tabs, so a second entry would be a second door to the
  // same page — and two doors are how the owner list drifted apart in the first
  // place. `?tab=settings` still resolves: staff are redirected to the merged
  // page (see OperatorConsole), owners keep their own settings there.
];

/**
 * Back-office tools, grouped behind a single "More" entry.
 *
 * Directory, Documents, Translations, Onboarding and Audit are things an admin
 * visits occasionally — unlike Tenants or Payments, which are the day job. On
 * the strip they cost a slot of permanent width each and pushed Support and
 * Onboarding off the edge on a laptop. Their routes are unchanged; only the
 * way you reach them moved.
 *
 * Role gates travel with the entries — Audit keeps requiresTechOrSuperAdmin,
 * Directory carries requiresStaffRole — and the menu filters on the same flags
 * the strip does (see moreItems), so moving anything here did not widen who can
 * see it.
 */
export const STAFF_MORE_NAV: RouteItem[] = [
  // First in the menu: it is the entry people go looking for BY NAME, having
  // been sent to find someone's account. Documents and Templates are browsed;
  // this one is searched for.
  {
    label: "Directory",
    i18nKey: "admin.nav.directory",
    icon: UsersRound,
    kind: "v2",
    slug: "directory",
    requiresStaffRole: true,
  },
  {
    label: "Documents",
    i18nKey: "admin.nav.documents",
    icon: FileText,
    kind: "legacy",
    slug: "documents",
  },
  // Templates are set up once and then left alone, so they cost more on the
  // strip than they return — they sit with the other occasional tools now.
  {
    label: "Templates",
    i18nKey: "admin.nav.templates",
    icon: FileText,
    kind: "legacy",
    slug: "contract-templates",
  },
  {
    label: "Translations",
    i18nKey: "admin.nav.translations",
    icon: Languages,
    kind: "v2",
    slug: "translations",
  },
  {
    label: "Onboarding",
    i18nKey: "admin.nav.owner-onboarding",
    icon: UserCheck,
    kind: "legacy",
    slug: "owner-onboarding",
  },
  {
    label: "Audit",
    i18nKey: "admin.nav.audit",
    icon: History,
    kind: "legacy",
    slug: "audit",
    requiresTechOrSuperAdmin: true,
  },
];

/**
 * The operator console (COL-352/Neel's rollout notes): the same admin surface,
 * "data-naked but feature-rich". Present = Analytics, Tenants, Owners,
 * Properties (V2), Support, Roles & access, Templates. Absent BY OMISSION =
 * Payments, Reconciliation, Reminders, Documents, Translations, Onboarding —
 * CoLiviq-internal money/back-office surfaces. Hiding here is UX only; the
 * hard gate is OPERATOR_CONSOLE_TABS in OperatorConsole plus operator RLS.
 * (Audit joins this list when the audit tab lands on this branch — its RLS
 * scoping already ships with COL-358.)
 */
export const OPERATOR_NAV: RouteItem[] = [
  {
    label: "Analytics",
    i18nKey: "admin.nav.analytics",
    icon: BarChart3,
    kind: "legacy",
    slug: "analytics",
  },
  {
    label: "Tenants",
    i18nKey: "admin.nav.tenants",
    icon: Users,
    kind: "legacy",
    slug: "tenants",
  },
  {
    label: "Owners",
    i18nKey: "admin.nav.owners",
    icon: UsersRound,
    kind: "v2",
    slug: "owners",
  },
  // No submissions badge — the approval queue is CoLiviq staff workflow.
  {
    label: "Properties",
    i18nKey: "admin.nav.properties",
    icon: Building2,
    kind: "v2",
    slug: "properties",
  },
  {
    label: "Support",
    i18nKey: "admin.nav.support",
    icon: Headset,
    kind: "legacy",
    slug: "issues",
    badge: "support",
  },
  // Reconciliation is a top-level operator tab (Neel: moved out of "More").
  {
    label: "Reconciliation",
    i18nKey: "admin.nav.reconciliation",
    icon: Landmark,
    kind: "v2",
    slug: "reconciliation",
  },
];

export const OPERATOR_MORE_NAV: RouteItem[] = [
  {
    label: "Templates",
    i18nKey: "admin.nav.templates",
    icon: FileText,
    kind: "legacy",
    slug: "contract-templates",
  },
  // Own-org activity only (audit_logs RLS operator arm) — no tech/super gate
  // here, the org boundary is the gate.
  {
    label: "Audit",
    i18nKey: "admin.nav.audit",
    icon: History,
    kind: "legacy",
    slug: "audit",
  },
  // Roles & access lives under "More" (Neel: moved in from the top row).
  {
    label: "Roles & access",
    i18nKey: "admin.nav.rolesAccess",
    icon: Settings,
    kind: "legacy",
    slug: "settings",
  },
];

/**
 * The Settings entry rendered by AdminUserMenu instead of the strip.
 *
 * MISLEADING NAME, DO NOT REPOINT: despite "STAFF", AdminUserMenu renders this
 * for `isOwnerPortalOnly` accounts ONLY — staff have no Settings entry in that
 * menu. So `?tab=settings` here resolves to AdminOwnerSettings: the owner's own
 * name, password and billing address.
 *
 * It deliberately did NOT follow the roles directory to /admin/owners. That
 * page redirects owner-portal accounts straight to /admin/properties, so
 * pointing this at it would leave owners with no route to their own settings
 * at all.
 */
export const STAFF_SETTINGS_NAV: RouteItem = {
  label: "Settings",
  i18nKey: "admin.nav.settings",
  icon: Settings,
  kind: "legacy",
  slug: "settings",
};

export function hrefFor(item: RouteItem): string {
  if (item.kind === "v2") return `/admin/${item.slug}`;
  return `/admin?tab=${item.slug}`;
}

export function isActive(
  item: RouteItem,
  pathname: string,
  searchParams: URLSearchParams,
  legacyDefaultTab: string
): boolean {
  if (item.kind === "v2") {
    const prefix = `/admin/${item.slug}`;
    return pathname === prefix || pathname.startsWith(prefix + "/");
  }
  // legacy: must be on /admin (exact, no trailing path) AND ?tab matches
  if (pathname !== "/admin") return false;
  const tab = searchParams.get("tab") ?? legacyDefaultTab;
  return tab === item.slug;
}

export function V2AdminNavbar() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const supportOpenCount = useSupportUnreadCount();
  // Hooks cannot run after the early return below, so this is resolved here.
  const ownerShellEnabled = useOwnerPortalV3();
  const { t } = useLanguage();
  const {
    loading: authLoading,
    isOwnerPortalOnly,
    isOperatorPortal,
    hasStaffRole,
    hasTechOrSuperAdmin,
  } = useAuth();
  // White-label operators never see the CoLiviq mark — their own name stands
  // in as text where staff and owners get the wordmark (COL-352).
  const { platformName: operatorBrandName } = useOperatorBranding();
  // Reconciliation is a staff-only entry — only fetch for staff accounts
  // (not owners, not operators).
  const reconciliationReviewCount = useReconciliationReviewCount(
    !authLoading && !isOwnerPortalOnly && !isOperatorPortal
  );
  // Only owners have a Notifications entry, so only fetch for owners.
  const ownerNotificationsUnread =
    useOwnerUnreadNotificationCount(isOwnerPortalOnly);
  // Owner submissions waiting on review. Not fetched in the owner portal —
  // the queue is a staff concern and an owner's own count is always their own.
  // Operators have no approval queue either.
  const { data: pendingSubmissions = 0 } = usePendingSubmissionCount(
    !isOwnerPortalOnly && !isOperatorPortal
  );
  // Open colivIQ cards. Staff-only, like the page itself.
  const brainBadge = useBrainBadgeCount(hasStaffRole && isAdminBrainEnabled());

  const navItems = useMemo(() => {
    // While roles resolve, hide role-gated entries rather than flashing them
    // at admins who won't keep them.
    if (authLoading) {
      return STAFF_NAV_ITEMS.filter(
        (i) => !i.retired && !i.requiresTechOrSuperAdmin
      );
    }
    if (isOwnerPortalOnly) {
      // The free-tier sections ship as empty states, so they stay behind a
      // flag rather than adding dead ends to every owner's nav today.
      return isOwnerPortalSectionsEnabled()
        ? OWNER_NAV
        : OWNER_NAV.filter((i) => !OWNER_FREE_TIER_SLUGS.has(i.slug));
    }
    if (isOperatorPortal) return OPERATOR_NAV;
    return STAFF_NAV_ITEMS.filter(
      (i) => !i.retired && (!i.requiresTechOrSuperAdmin || hasTechOrSuperAdmin)
    );
  }, [authLoading, isOwnerPortalOnly, isOperatorPortal, hasTechOrSuperAdmin]);

  /**
   * The More menu is role-gated the same way the strip is: Audit only shows
   * for tech/super admins, Directory only for staff roles, and both stay hidden
   * while roles are still resolving.
   */
  const moreItems = useMemo(() => {
    if (authLoading) {
      return STAFF_MORE_NAV.filter(
        (i) => !i.requiresTechOrSuperAdmin && !i.requiresStaffRole
      );
    }
    return STAFF_MORE_NAV.filter(
      (i) =>
        (!i.requiresTechOrSuperAdmin || hasTechOrSuperAdmin) &&
        (!i.requiresStaffRole || hasStaffRole)
    );
  }, [authLoading, hasTechOrSuperAdmin, hasStaffRole]);

  /**
   * What the horizontal strip shows. Owners keep Settings out of it — the
   * account dropdown already carries it, and the strip is meant to hold the
   * places an owner works: Analytics, My properties, Notifications.
   */
  const stripItems = useMemo(
    () =>
      isOwnerPortalOnly
        ? navItems.filter((i) => i.slug !== STAFF_SETTINGS_NAV.slug)
        : navItems,
    [isOwnerPortalOnly, navItems]
  );

  const legacyDefaultTab = "analytics";

  const labelFor = (item: RouteItem) =>
    item.i18nKey ? t(item.i18nKey, item.label) : item.label;

  const badgeCounts: Record<NavBadge, number | null> = {
    support: supportOpenCount,
    ownerNotifications: ownerNotificationsUnread,
    reconciliation: reconciliationReviewCount,
    submissions: pendingSubmissions,
    brain: brainBadge.count,
  };
  const badgeAria: Record<NavBadge, string> = {
    support: t("admin.nav.openSupportItems", "open support items"),
    ownerNotifications: t(
      "admin.nav.unreadNotifications",
      "unread notifications"
    ),
    reconciliation: t("admin.nav.needsReviewMatches", "matches needing review"),
    submissions: t(
      "admin.nav.pendingSubmissions",
      "properties awaiting review"
    ),
    brain: t("admin.nav.brainBadgeAria", "open items in colivIQ"),
  };

  /**
   * Owner portal V3 (COL-528): owners get a left sidebar instead of this
   * strip. Every owner surface mounts this component — the ~20 AdminV2Layout
   * pages and the four that mount it directly — so swapping here reaches all
   * of them, including the legacy console at /admin?tab=analytics, without
   * touching the staff and operator chrome those same pages render.
   *
   * Below the tablet breakpoint the sidebar collapses to a top bar with a
   * drawer (PRD question 11's default); it owns both, so nothing here needs a
   * narrow-screen branch.
   */
  const ownerShell = isOwnerPortalOnly && ownerShellEnabled;
  if (ownerShell) {
    return (
      <OwnerPortalSidebar
        items={stripItems}
        labelFor={labelFor}
        notificationCount={ownerNotificationsUnread}
        legacyDefaultTab={legacyDefaultTab}
      />
    );
  }

  return (
    <div className="border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30">
      {/* Same width as AdminV2Layout's shell — a narrower strip would sit
          inset from the page content it labels on anything over 1536px. */}
      <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 lg:px-8">
        {/* Three-section layout: brand on the left (logo + Admin badge),
            scrollable tab list in the middle, fixed user-menu on the right.
            The middle block flexes; the brand and user-menu are shrink-0
            so the tab strip is the only thing that compresses on narrow
            screens. */}
        <div className="flex items-center h-12 gap-2">
          {/* Brand. The coliviq wordmark image, clicking through to public
              home (/) like the public nav; admins use the Account dropdown to
              reach /dashboard or /admin sections, not the logo. An OPERATOR
              portal shows no coliviq mark at all (COL-352): white-label
              operators keep a neutral text brand. */}
          <Link
            to="/"
            className="flex items-center gap-2 leading-none shrink-0 mr-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            title="coliviq home"
          >
            {isOperatorPortal ? (
              <span className="text-base font-bold text-petrol leading-none">
                {operatorBrandName}
              </span>
            ) : (
              <BrandWordmark className="h-5 w-auto" />
            )}
          </Link>
          {/* Hide until roles resolve — avoids flashing "Admin" for owner-only accounts. */}
          {!authLoading ? (
            <span
              className={cn(
                "hidden md:inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded shrink-0",
                isOwnerPortalOnly
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground bg-muted/60"
              )}
            >
              {isOwnerPortalOnly
                ? t("admin.nav.ownerPortalBadge", "Owner portal")
                : hasStaffRole
                  ? t("admin.nav.label", "Admin")
                  : t("admin.nav.operatorBadge", "Operator")}
            </span>
          ) : null}
          {/* Both audiences get the horizontal strip. Owner nav used to live
              only inside the profile dropdown, which left owners with a bare
              navbar and no visible way between Analytics, My properties and
              Notifications. Settings is filtered out of the owner strip (see
              stripItems) because it still belongs in the account dropdown. */}
          <nav
            className="flex-1 min-w-0 flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label={
              authLoading || hasStaffRole
                ? t("admin.nav.label", "Admin")
                : isOwnerPortalOnly
                  ? t("admin.nav.ownerPortalBadge", "Owner portal")
                  : t("admin.nav.operatorBadge", "Operator")
            }
          >
            {stripItems.map((item) => {
              const active = isActive(
                item,
                pathname,
                searchParams,
                legacyDefaultTab
              );
              const pip = item.badge
                ? navPip(
                    badgeCounts[item.badge],
                    badgeAria[item.badge],
                    item.badge === "brain" && brainBadge.incomplete
                      ? t(
                          "admin.nav.brainBadgeIncomplete",
                          "colivIQ: some cards couldn't load"
                        )
                      : null
                  )
                : null;
              const label = labelFor(item);
              return (
                <Link
                  key={`${item.kind}:${item.slug}`}
                  to={hrefFor(item)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors shrink-0",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    // Owners get the filled pill from the portal design;
                    // staff keep the quieter grey the dense strip needs.
                    active
                      ? isOwnerPortalOnly
                        ? "bg-petrol text-white font-semibold"
                        : "bg-muted text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                  )}
                  aria-current={active ? "page" : undefined}
                  title={
                    item.kind === "v2"
                      ? `${label} (V2 admin)`
                      : `${label} (Operator Console)`
                  }
                >
                  <span>{label}</span>
                  {pip ? (
                    <span
                      className="h-[16px] min-w-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center leading-none"
                      aria-label={pip.ariaLabel}
                    >
                      {pip.text}
                    </span>
                  ) : null}
                </Link>
              );
            })}

            {/* One grouped entry: the occasional back-office tools. Owner-portal
                accounts see none of it — every item in there is staff-only.
                Operators get their own reduced set (Templates). */}
            {!isOwnerPortalOnly && (
              <NavGroup
                label={t("admin.nav.more", "More")}
                items={isOperatorPortal ? OPERATOR_MORE_NAV : moreItems}
                pathname={pathname}
                searchParams={searchParams}
                legacyDefaultTab={legacyDefaultTab}
                labelFor={labelFor}
              />
            )}
          </nav>
          {!authLoading && hasStaffRole ? <SupportNotificationsBell /> : null}
          {/* Owner nav is on the strip now, not in this dropdown — passing
              ownerNavItems here too would list every section twice. Dropping
              it also lets the shared Settings entry render for owners. */}
          <AdminUserMenu
            pathname={pathname}
            searchParams={searchParams}
          />
        </div>
      </div>
      {/* Directly under the strip so every admin screen a Brain card links
          to — V2 pages and legacy ?tab= panels alike — gets the way back. */}
      <BrainReturnStrip />
    </div>
  );
}

/**
 * One strip entry standing for several pages. Highlights whenever the current
 * route is any of its children — without that, opening a grouped page leaves
 * the whole strip looking inactive, with no clue where you are.
 */
function NavGroup({
  label,
  items,
  pathname,
  searchParams,
  legacyDefaultTab,
  labelFor,
}: {
  label: string;
  items: RouteItem[];
  pathname: string;
  searchParams: URLSearchParams;
  legacyDefaultTab: string;
  labelFor: (item: RouteItem) => string;
}) {
  const groupActive = items.some((item) =>
    isActive(item, pathname, searchParams, legacyDefaultTab)
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors shrink-0",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            groupActive
              ? "bg-muted text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
          )}
          aria-current={groupActive ? "page" : undefined}
        >
          <span>{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-40">
        {items.map((item) => {
          const active = isActive(
            item,
            pathname,
            searchParams,
            legacyDefaultTab
          );
          return (
            <Link key={`${item.kind}:${item.slug}`} to={hrefFor(item)}>
              <DropdownMenuItem
                className={cn(
                  "cursor-pointer",
                  active && "bg-muted font-medium"
                )}
              >
                {labelFor(item)}
              </DropdownMenuItem>
            </Link>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
